import React, {
	useImperativeHandle,
	useMemo,
	useRef
} from 'react';
import cn from 'classnames';

import {EEventSource} from 'coreClasses/events/IEvents';
import {useLocation, useProduct, useUI} from 'coreHooks';

import CalloutMessagesPlp from 'brandComponents/CalloutMessagesPlp';
import Link from 'brandComponents/Link';
import AddToWishlistButton from 'brandComponents/product/AddToWishlistButton';
import {EProductMediaType} from 'brandComponents/product/ProductMedia';
import ProductSizes from 'brandComponents/product/ProductSizes';
import ProductHit, {
	ProductHitBadges,
	ProductHitComponent,
	ProductHitCta,
	ProductHitDescription,
	ProductHitMedia,
	ProductHitName,
	ProductHitPrice,
	ProductHitWishlistInfo,
} from 'brandComponents/ProductHit';
import productStyles from 'brandComponents/ProductHit/ProductHit.module.scss';
import {DEFAULT_PRODUCT_HIT_COMPONENTS} from 'brandConstants';
import {useProductVisibilityChange} from 'brandHooks/useProductVisibilityChange';

import {IProductTileProps} from './IProductTile';

import styles from './ProductTile.module.scss';

const ProductHitMediaContainer = (
	{
		customProductMediaType,
		primaryCategoryId,
		carouselConfig,
		source,
		showPlaceholder,
		priority,
	}) => {
	const isServerUI = useUI('isServer');
	const localShowPlaceholder = useMemo(() => {
		if (typeof showPlaceholder !== 'boolean') {
			return !isServerUI;
		} else {
			return showPlaceholder;
		}
	}, [showPlaceholder, isServerUI]);

	return (
		<ProductHitMedia
			mediaType={customProductMediaType || EProductMediaType.CAROUSEL}
			sourceSpecification={primaryCategoryId}
			carouselConfig={carouselConfig}
			isLink
			source={source}
			showPlaceholder={localShowPlaceholder}
			priority={priority}
		/>
	);
};

const ProductTile = ({
	searchHit,
	productId: _productId,
	index,
	productHitComponents = DEFAULT_PRODUCT_HIT_COMPONENTS as ProductHitComponent[],
	classNames,
	customProductMediaType,
	productTitleTag = 'span',
	carouselConfig,
	hasDescription = false,
	productCoverMedia = null,
	hideReadMore,
	hasCta = false,
	showWishlistInfo = false,
	showAddToWishlist = true,
	onClick,
	sourceOverwritten,
	showPlaceholder,
	priority = false,
	ref,
}: IProductTileProps) => {
	const tileRef = useRef<HTMLDivElement>();
	const location = useLocation();
	const isHomepage = location?.isHomePage;
	const productId = _productId || searchHit?.getProductId();
	const productModel = useProduct(productId);
	const product = productModel?.isLoaded() || !searchHit ? productModel : searchHit;
	const source = isHomepage ? EEventSource.HOME : EEventSource.PLP;
	const color = productModel?.getColorDescription();
	const size = productModel?.getSize();

	useProductVisibilityChange(tileRef, product?.getProductId());

	useImperativeHandle(ref, () => tileRef.current, []);

	return (
		<ProductHit
			ref={tileRef}
			product={product}
			eventOnClick={onClick}
			position={index}
			classNames={cn(styles['product-tile'], classNames, 'product-tile')}
			sourceOverwritten={sourceOverwritten}
		>
			{showAddToWishlist && productId && (
				<div className={cn(styles['add-to-wishlist'], 'add-to-wishlist-tile')}>
					<AddToWishlistButton productId={productId} aggregateVariationGroup={false} />
				</div>
			)}
			{productHitComponents.includes('Media') &&
				(productCoverMedia ? (
					<div className={cn(styles['product-hit__media-cover'], 'product-hit__media-cover')}>
						<Link to={product?.getProductUrl()} className="contact-us">
							{productCoverMedia}
						</Link>
					</div>
				) : (
					<ProductHitMediaContainer
						customProductMediaType={customProductMediaType}
						primaryCategoryId={product?.getPrimaryCategoryId()}
						carouselConfig={carouselConfig}
						source={source}
						showPlaceholder={showPlaceholder}
						priority={priority}
					/>
				))}

			<div className={cn(productStyles['product-hit__text-container'], 'product-hit__text-container')}>
				{productHitComponents.includes('Name') && (
					<ProductHitName as={productTitleTag} sourceSpecification={product?.getPrimaryCategoryId()} source={source} />
				)}

				{product?.getVariants?.() && <ProductSizes product={product} />}

				{productHitComponents.includes('Price') && <ProductHitPrice />}

				{showWishlistInfo && <ProductHitWishlistInfo color={color} size={size} />}

				{hasDescription && productHitComponents.includes('Description') && <ProductHitDescription hideReadMore={hideReadMore} />}

				{productHitComponents.includes('Badges') && (
					<ProductHitBadges classNames={cn(productStyles['product-hit__badges'], 'product-hit__badges')} />
				)}

				{hasCta && <ProductHitCta classNames={cn(productStyles['product-hit__link'], 'product-hit__link')} />}
			</div>
			<CalloutMessagesPlp product={product} />
		</ProductHit>
	);
};

export default ProductTile;
